import React from 'react';
import Mountpoint from './Mountpoint';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
const {DESIGNER} = WebflowEnvironments;
import {
  Plugins,
  Atom,
  withSystem,
  AttributesObject,
} from '@packages/systems/wfdl';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  TListOfElement,
  TRecord,
  TList,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  usePreviewMode,
  useOpenCanvas,
  useRuntimeEnvironment,
} from '@packages/systems/render';
import {EvaluatedNodeData, getChildren} from '@packages/utilities/data-utils';

const Body = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    data,
    renderChild,
    environmentContext,
  }) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    const fragmentProps = usePreviewMode() ? {key: 'preview'} : {};
    const isOpenCanvas = useOpenCanvas();

    const bodyEventHandlers: Record<string, unknown> | null =
      runtimeEnvironment === DESIGNER
        ? environmentContext.get('bodyEventHandlers')
        : null;

    const bodyContent = (
      <React.Fragment>
        {/* `key` prop makes sure React re-mounts the content of Body when user
      exists Preview mode. It makes sure React can continue rendering the
      canvas correctly even if any site module has manipulated the DOM (e.g.
      by re-rendering a Collection List with different number of items). */}
        <React.Fragment {...fragmentProps}>
          {getChildren(data).map((child: EvaluatedNodeData) =>
            renderChild(child)
          )}
        </React.Fragment>
        {runtimeEnvironment === DESIGNER && (
          <Mountpoint id="lightbox-mountpoint" />
        )}
      </React.Fragment>
    );

    const BodyTag = getBodyTag(runtimeEnvironment, isOpenCanvas);

    return (
      <BodyTag
        attributes={generateAttributes(attributes, classes)}
        bodyEventHandlers={bodyEventHandlers}
      >
        {bodyContent}
      </BodyTag>
    );
  }
);

const config = Atom.create({
  displayName: 'Body',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Body'), iconName('Body')),
  },
  showEmpty: false,

  lockedStyles: {
    float: {when: true, message: 'The body can’t be floated.'},
    opacity: {
      when: true,
      message:
        'Opacity is disabled on the body as it would affect all site elements, and break the entire site on some devices.',
    },
    position: {
      when: true,
      message: 'The body’s position can’t be edited.',
    },
    width: {
      when: true,
      message: 'The browser automatically sets the body’s width.',
    },
    // FIXME - not in $Keys<WebflowCssProperties> *
    // @ts-expect-error - TS2322 - Type '{ float: { when: true; message: string; }; opacity: { when: true; message: string; }; position: { when: true; message: string; }; width: { when: true; message: string; }; displayInline: { when: true; message: string; }; displayInlineBlock: { ...; }; displayNone: { ...; }; }' is not assignable to type 'Partial<Record<keyof WebflowCssProperties, LockedStyleType>>'.
    displayInline: {
      when: true,
      message: "The body element's Display style cannot be inline.",
    },
    // FIXME - not in $Keys<WebflowCssProperties> *
    displayInlineBlock: {
      when: true,
      message: "The body element's Display style cannot be inline-block.",
    },
    // FIXME - not in $Keys<WebflowCssProperties> *
    displayNone: {
      when: true,
      message: "The body element's Display style cannot be none.",
    },
  },
  render: Body,
  dataType: TRecord(
    {
      children: TListOfElement,
      xattr: withSystem(
        {label: 'Custom attributes', private: true},
        TList(
          TTypeConstructor(
            qualify(pluginName('Basic'), typeName('CustomAttribute'))
          )
        )
      ),
    },
    extraAtomFields
  ),
});

type BodyTagProps = React.PropsWithChildren<{
  attributes: AttributesObject;
  bodyEventHandlers: Record<string, unknown> | null;
}>;

/**
 * The default body element to use when rendering the Body atom.
 */
const DefaultBodyTag = ({
  children,
  attributes,
  bodyEventHandlers,
}: BodyTagProps) => {
  return (
    <body {...bodyEventHandlers} {...attributes}>
      {children}
    </body>
  );
};

/**
 * A `div` element that is used as a "proxy" for the user's body element when the Open Canvas is active.
 *
 * When the Open Canvas is active, we take over the `body` as the top-level
 * root element of the iframe document, where the Canvas is rendered. Since multiple `body` elements
 * are not allowed in HTML, this `div` element is used for rendering.
 *
 * Additionally, the `bodyEventHandlers` are intentionally NOT attached to the component rendered
 * by this Atom. Instead, it is attached to the Open Canvas' owned body.
 */
const OpenCanvasBodyTag = ({children, attributes}: BodyTagProps) => {
  return (
    <div
      data-wf-open-canvas-body-element-proxy="true"
      {...attributes}
      style={{
        ...attributes.style,
        isolation: 'isolate', // ensure the body proxy creates a new stacking context, so that z-index styles on body elements work as expected
        flexGrow: 1,
      }}
    >
      {children}
    </div>
  );
};

const getBodyTag = (runtimeEnvironment: Environment, isOpenCanvas: boolean) => {
  if (runtimeEnvironment === DESIGNER && isOpenCanvas) {
    return OpenCanvasBodyTag;
  } else {
    return DefaultBodyTag;
  }
};

export const forTestSuite = {
  DefaultBodyTag,
  OpenCanvasBodyTag,
  getBodyTag,
};

export default config;

// * See https://github.com/webflow/webflow/issues/20451
